import { Sentry, instrument, setRouteAttributes } from '$lib/services/sentry';
import { goto } from '$app/navigation';
import * as DeviceId from '$lib/utils/device-id.js';
import {
	enableEventDeferral,
	flushDeferredEvents,
	disableEventDeferral,
} from '$lib/utils/device-id.js';
import { app, setGlobalStore } from '$lib/stores/app/store.js';
import { setMeta } from '$lib/stores/app/actions.js';
import Cookies from 'js-cookie';
import { AnalyticsService } from '$lib/services/analytics/index.js';
import { Analytics } from '$lib/services/analytics/client';
import { collectSessionInitData } from '$lib/services/analytics/session-init';
import { EVENT_TYPES } from '$events';
import { qualifyFeatureFlags } from '$lib/services/experiments/index.js';
import { ProductExperienceService } from '$lib/services/product-experience/index.js';
import { validateUser, tokenIntrospect } from '$lib/stores/user/action.js';
import { clearVisitedPaths } from '$lib/services/auth';
import {
	PAGES,
	QUERY_PARAM_KEYS,
	SENTRY_ERROR_LEVELS,
	LINK_FROM_APP_PLATFORMS,
	COOKIES,
	AUTH_COHORT,
	AUTH_OUTCOME,
} from '$lib/utils/constants.js';
import { getEnvironment } from '$lib/utils/environment';
import { isEntryFromAltPaymentAppLink } from '$lib/utils/link-from-app-platform';
import { get } from 'svelte/store';
import type { InboundData } from '$lib/services/inbound-gateway/validation';
import { ApiService } from '$lib/services/api/index.js';
import { LOGIN_ERROR_TYPES, LOGIN_URL_QUERY_KEYS } from '../../../routes/login/__config/common';
import APIError from '../api/error';

const TOKEN_INTROSPECT_RESULT = {
	CONTINUE: 'continue',
	REDIRECT_TO_LOGIN: 'redirect_to_login',
} as const;
type TokenIntrospectResult = (typeof TOKEN_INTROSPECT_RESULT)[keyof typeof TOKEN_INTROSPECT_RESULT];
import { QUERY_PARAM_KEYS as PAYPAL_QUERY_PARAM_KEYS } from '@mkt-client/payment-providers/paypal';

/**
 * App Initialization Service
 *
 * Manages the complete app initialization flow including device ID setup,
 * authentication (token introspection and user validation), meta initialization,
 * experiments, and product experience configuration.
 *
 * Also provides a mechanism to ensure hooks.client.js initialization completes
 * before route policies and other client-side code execute, solving the race
 * condition where route load functions may execute before hooks.client.js init()
 * completes.
 */
export class AppInitializationService {
	// Private state for initialization promise management
	private static initializationPromise: Promise<void> | null = null;
	private static initializationResolve: (() => void) | null = null;
	private static isInitialized = false;

	/**
	 * performance.now() captured the first time a route calls
	 * `waitForInitialization()` while initialization is still in flight. Used
	 * to measure the route-blocking gate separately from the broader
	 * `app.init` span. Null when no waiter has registered or after init.
	 */
	private static initGateStartMs: number | null = null;

	/**
	 * Creates and returns a promise that resolves when app initialization completes.
	 * Multiple calls return the same promise instance. The first call records
	 * the gate-start timestamp so the route-blocking duration can be measured.
	 * @returns {Promise<void>} Promise that resolves when initialization is complete
	 */
	public static waitForInitialization(): Promise<void> {
		if (this.isInitialized) {
			return Promise.resolve();
		}

		if (
			this.initGateStartMs === null &&
			typeof performance !== 'undefined' &&
			typeof performance.now === 'function'
		) {
			this.initGateStartMs = performance.now();
		}

		if (!this.initializationPromise) {
			this.initializationPromise = new Promise<void>((resolve) => {
				this.initializationResolve = resolve;
			});
		}

		return this.initializationPromise;
	}

	/**
	 * Marks app initialization as complete.
	 * This should be called from hooks.client.js after all initialization completes.
	 * Emits the route-blocking init-gate duration on the active transaction.
	 * @returns {void}
	 */
	public static markInitializationComplete(): void {
		if (this.isInitialized) {
			// Already initialized, ignore duplicate calls
			return;
		}

		this.isInitialized = true;

		this.emitInitGateDuration();

		if (this.initializationResolve) {
			this.initializationResolve();
			this.initializationResolve = null;
			this.initializationPromise = null;
		}
	}

	/**
	 * Emits a `time_to_init_gate` measurement for the route-blocking gate
	 * duration (first `waitForInitialization` call -> `markInitializationComplete`).
	 * Best-effort: no-ops when no gate start was recorded or tracing is unavailable;
	 * failures are reported to Sentry and never rethrown.
	 * @returns {void}
	 */
	private static emitInitGateDuration(): void {
		try {
			if (this.initGateStartMs === null) return;
			if (typeof performance === 'undefined' || typeof performance.now !== 'function') return;
			const duration = performance.now() - this.initGateStartMs;
			this.initGateStartMs = null;
			if (typeof Sentry.setMeasurement === 'function') {
				Sentry.setMeasurement('time_to_init_gate', duration, 'millisecond');
			}
		} catch (error) {
			Sentry.captureMessage('AppInit: Failed to emit init-gate measurement', {
				level: SENTRY_ERROR_LEVELS.WARNING,
				extra: {
					error: error,
				},
			});
		}
	}

	/**
	 * Resets the initialization state (useful for testing).
	 * @returns {void}
	 */
	public static resetInitialization(): void {
		this.isInitialized = false;
		this.initializationPromise = null;
		this.initializationResolve = null;
		this.initGateStartMs = null;
	}

	/**
	 * Main initialization method that runs all app initialization logic.
	 * This should be called from hooks.client.js init() function.
	 * @returns {Promise<void>}
	 */
	public static async initialize(): Promise<void> {
		await instrument('app.init', 'App initialization', async (appInitSpan) => {
			const params = new URLSearchParams(window.location.search);
			// Enable event deferral before device ID initialization to ensure validateUser fires first
			// TODO: remove this once we have refactored the device id logic away from cookies - AD
			enableEventDeferral();
			const { deviceId, isFirstTimeVisit } = await instrument(
				'app.init.device_id',
				'Initialize device id',
				() => this.initializeDeviceId()
			);

			// clear visited paths if user refreshed the page
			clearVisitedPaths();

			// Ensure product experience is set from URL
			await instrument('app.init.product_experience', 'Set product experience from query', () =>
				ProductExperienceService.setProductExperienceFromQuery(false)
			);

			await instrument('app.init.meta', 'Initialize meta', () => this.initializeMeta(deviceId));

			const url = new URL(window.location.href);
			const isInboundGateway = url.pathname.includes(PAGES.INTERNAL.INBOUND_GATEWAY);
			const inboundPayload = await this.parseInboundPayload(url, isInboundGateway);

			await instrument('app.init.global_store', 'Initialize global store', () =>
				this.initializeGlobalStore(params, isFirstTimeVisit, inboundPayload)
			);

			const introspectionResult = await instrument(
				'app.init.token_introspection',
				'Token introspection',
				() => this.handleTokenIntrospection(url, isInboundGateway, inboundPayload)
			);

			const plogToken = await instrument('app.init.plog_upgrade', 'Plog session upgrade', () =>
				this.handlePlogSessionUpgrade()
			);
			// Skip the redundant User/ValidateLogin request when Token/Introspect
			// already hydrated the user store (URL-token path) and no plog-upgrade
			// token supersedes it. Plog upgrades produce a fresh token that still
			// requires a separate validation request.
			const skipValidation = introspectionResult.validated && !plogToken;
			await instrument('app.init.validate_user', 'Validate user', () =>
				this.handleValidateUser(plogToken, skipValidation)
			);

			// Attach bounded auth cohort/outcome attributes to the active
			// transaction so the init gate can be sliced by auth path in
			// Explore. Keep the existing app.init.* child spans for diagnosis.
			this.setAuthAttributes(introspectionResult, plogToken, skipValidation);

			await instrument('app.init.experiments', 'Initialize feature flags', () =>
				this.initializeFeatureFlags()
			);

			// Best-effort grouping attributes (never break init): lets us slice
			// app-init latency by platform / cohort in Explore > Traces.
			try {
				const appState = get(app);
				appInitSpan?.setAttributes({
					'abc.in_app': Boolean(appState?.meta?.inApp),
					'abc.link_from_app_platform': String(appState?.global?.isLinkFromAppPlatform ?? 'none'),
					'abc.product_experience': ProductExperienceService.AOFL_PRODUCT_NAME,
					'abc.is_first_time_visit': Boolean(isFirstTimeVisit),
					'abc.experiment_count': Object.keys(appState?.global?.experiments ?? {}).length,
				});
			} catch {
				// attributes are best-effort
			}

			// Mark initialization complete after all initialization logic finishes
			// This ensures route policies wait for global initialization (including validateUser, setMeta, experiments, etc.) before executing
			this.markInitializationComplete();

			await this.emitSessionInit();

			if (introspectionResult.outcome === TOKEN_INTROSPECT_RESULT.REDIRECT_TO_LOGIN) {
				await this.redirectToLoginAfterIntrospectFailure();
			}
		});
	}

	/**
	 * Emits the `client.session_init` event with stable session metadata
	 * (app, device, geo, settings, experiments, feature flags,
	 * link-from-app). Fires exactly once per `initialize()` call, after
	 * `markInitializationComplete()` so experiments and user state are
	 * fully resolved.
	 *
	 * Wrapped in `try/catch` — a session-init failure must never break
	 * app init. The error degrades to a Sentry warning and the user
	 * continues normally.
	 * @returns {Promise<void>}
	 */
	private static async emitSessionInit(): Promise<void> {
		try {
			const data = await collectSessionInitData();
			await Analytics.log(EVENT_TYPES.CLIENT_SESSION_INIT, data, {
				safeAction: 'SessionInit: envelope build failed',
			});
		} catch (error) {
			Sentry.captureException(error, {
				extra: { error: String(error) },
			});
		}
	}

	/**
	 * Attaches bounded auth cohort/outcome attributes to the active transaction.
	 * Cohort identifies which auth path ran; outcome identifies its result. Both
	 * are low-cardinality strings so they stay cheap to slice in Explore.
	 * @param {{ outcome: TokenIntrospectResult; validated: boolean }} introspectionResult - Token introspection result.
	 * @param {TokenIntrospectResult} introspectionResult.outcome - Whether init should continue or redirect to login.
	 * @param {boolean} introspectionResult.validated - Whether the user store was hydrated from introspection.
	 * @param {string | null} plogToken - Optional JWT from a plog session upgrade, or null.
	 * @param {boolean} skipValidation - When true, ValidateLogin was skipped because introspection hydrated the user store.
	 * @returns {void}
	 */
	private static setAuthAttributes(
		introspectionResult: { outcome: TokenIntrospectResult; validated: boolean },
		plogToken: string | null,
		skipValidation: boolean
	): void {
		const url = new URL(window.location.href);
		const isPasswordReset = url.pathname.includes(PAGES.INTERNAL.RESET_PASSWORD);
		const isMagicLinkRedeem = url.pathname.includes(PAGES.INTERNAL.UNIVERSAL.MAGIC_LINK_REDEEM);
		const isInboundGateway = url.pathname.includes(PAGES.INTERNAL.INBOUND_GATEWAY);

		let cohort: string;
		if (plogToken) cohort = AUTH_COHORT.PLOG_UPGRADE;
		else if (isPasswordReset) cohort = AUTH_COHORT.PASSWORD_RESET;
		else if (isMagicLinkRedeem) cohort = AUTH_COHORT.MAGIC_LINK_REDEEM;
		else if (introspectionResult.validated || isInboundGateway) cohort = AUTH_COHORT.URL_TOKEN;
		else cohort = AUTH_COHORT.SESSION;

		let outcome: string;
		if (introspectionResult.outcome === TOKEN_INTROSPECT_RESULT.REDIRECT_TO_LOGIN) {
			outcome = AUTH_OUTCOME.REDIRECTED_TO_LOGIN;
		} else if (skipValidation) {
			outcome = AUTH_OUTCOME.VALIDATED_SKIP;
		} else if (introspectionResult.validated) {
			outcome = AUTH_OUTCOME.VALIDATED;
		} else {
			outcome = AUTH_OUTCOME.SESSION;
		}

		setRouteAttributes({
			'abc.auth_cohort': cohort,
			'abc.auth_outcome': outcome,
		});
	}

	/**
	 * Initializes device ID and returns device ID along with first-time visit status.
	 * Handles migration, initialization, error handling, and fallback device ID generation.
	 * @returns {Promise<{ deviceId: string; isFirstTimeVisit: boolean }>}
	 */
	private static async initializeDeviceId(): Promise<{
		deviceId: string;
		isFirstTimeVisit: boolean;
	}> {
		DeviceId.migrateDeviceIdAndFixErroneousValues();

		const isFirstTimeVisit = !DeviceId.getDeviceId();
		const isDeviceIdInitialized = await DeviceId.initializeDeviceId();

		if (!isDeviceIdInitialized) {
			DeviceId.fireDeviceIdFailureEvent(DeviceId.DEVICE_ID_FAILURE_TYPES.init_failure);
		}

		let deviceId = DeviceId.getDeviceId();

		if (!deviceId) {
			DeviceId.fireDeviceIdFailureEvent(DeviceId.DEVICE_ID_FAILURE_TYPES.get_failure);
			deviceId = 'client_failure:on_app_init:hooks_client.js';
		}

		return { deviceId, isFirstTimeVisit };
	}

	/**
	 * Handles token introspection from URL params.
	 * Performs token introspection from URL params (TOKEN, USER_TOKEN, or PAYLOAD).
	 * Skips token introspection for password-reset route (uses recovery token, not auth token).
	 * @param {URL} url - Current page URL
	 * @param {boolean} isInboundGateway - Whether the current route is inbound-gateway
	 * @param {InboundData | null} inboundPayload - Pre-validated inbound-gateway payload
	 * @returns {Promise<{ outcome: TokenIntrospectResult; validated: boolean }>} Whether app init should continue or redirect to login, and whether the user store was already hydrated from the introspection response.
	 */
	private static async handleTokenIntrospection(
		url: URL,
		isInboundGateway: boolean,
		inboundPayload: InboundData | null
	): Promise<{
		outcome: TokenIntrospectResult;
		validated: boolean;
	}> {
		const shouldPassToken =
			isInboundGateway ||
			url.pathname.includes(PAGES.INTERNAL.LINKS.QR_ENTRY) ||
			url.pathname.includes(PAGES.INTERNAL.LINKS.LINK_ENTRY) ||
			url.searchParams.has(PAYPAL_QUERY_PARAM_KEYS.PAYPAL_REDIRECT_FLAG);
		const isPasswordReset = url.pathname.includes(PAGES.INTERNAL.RESET_PASSWORD);
		const isMagicLinkRedeem = url.pathname.includes(PAGES.INTERNAL.UNIVERSAL.MAGIC_LINK_REDEEM);
		// Mirror the device_id query-param semantics currently applied by
		// handleValidateUser so introspection carries the right client_context
		// when it replaces the redundant ValidateLogin request.
		const preserveSessionDeviceId = !url.searchParams.has(QUERY_PARAM_KEYS.DEVICE_ID);

		// Skip token introspection for password-reset route (uses recovery token, not auth token)
		if (!isPasswordReset && !isMagicLinkRedeem) {
			// Centralized token introspection for all routes
			// Handles three token sources: TOKEN, USER_TOKEN, and PAYLOAD (for inbound-gateway)
			try {
				return await this.introspectTokenFromUrl(
					shouldPassToken,
					preserveSessionDeviceId,
					inboundPayload
				);
			} catch (error) {
				Sentry.captureMessage('AppInit: Token introspection failed', {
					level: SENTRY_ERROR_LEVELS.FATAL,
					extra: {
						error: error,
					},
				});
			}
		}
		return { outcome: TOKEN_INTROSPECT_RESULT.CONTINUE, validated: false };
	}

	/**
	 * Handles plog session upgrade for reactivation route.
	 * Fixes issue where 1.0 inactive Unity users are redirected from the student homepage to the reactivation page with an unauthenticated webview session and getting kicked out to the home page.
	 * @returns {Promise<string | null>} The JWT token if successful, null otherwise
	 */
	private static async handlePlogSessionUpgrade(): Promise<string | null> {
		if (get(app)?.meta?.inApp && window.location.href.includes(PAGES.INTERNAL.REACTIVATION)) {
			Sentry.captureMessage('AppInit: plog session upgrade cookies', {
				level: SENTRY_ERROR_LEVELS.INFO,
				extra: {
					href: window.location.href,
					cookies: Cookies.get(),
					documentCookies: document.cookie,
				},
			});

			if (!Cookies.get(COOKIES.PLOG)) {
				Sentry.captureMessage('AppInit: plog cookie not found', {
					level: SENTRY_ERROR_LEVELS.FATAL,
				});
				return null;
			}

			try {
				const response = await ApiService.upgradeSession();
				// @ts-expect-error - response type is not fully typed, but contains token property
				return response?.token || null;
			} catch (error) {
				Sentry.captureMessage('AppInit: plog session upgrade failed', {
					level: SENTRY_ERROR_LEVELS.FATAL,
					extra: {
						error: error,
					},
				});
			}
		}
		return null;
	}

	/**
	 * Handles user validation.
	 * Validates user state and flushes deferred device ID events.
	 * @param {string | null} token - Optional token from plog session upgrade to pass to validateUser
	 * @param {boolean} [skipValidation] - When true, the ValidateLogin request is skipped because Token/Introspect already hydrated the user store. Deferred device ID events are still flushed and event deferral is disabled.
	 * @returns {Promise<void>}
	 */
	private static async handleValidateUser(
		token: string | null,
		skipValidation = false
	): Promise<void> {
		if (!skipValidation) {
			const url = new URL(window.location.href);

			// Only set preserveSessionDeviceId to false if deviceId query param exists
			const hasDeviceIdQueryParam = url.searchParams.has(QUERY_PARAM_KEYS.DEVICE_ID);
			/** @type {{ preserveSessionDeviceId?: boolean; token?: string }} */
			const validateUserOptions: { preserveSessionDeviceId?: boolean; token?: string } = {};

			if (hasDeviceIdQueryParam) {
				validateUserOptions.preserveSessionDeviceId = false;
			}

			// If plog session upgrade returned a token, pass it to validateUser
			if (token) {
				validateUserOptions.token = token;
			}

			try {
				// @ts-expect-error - validateUser accepts options with optional preserveSessionDeviceId
				await validateUser(validateUserOptions);
			} catch (error) {
				Sentry.captureMessage('AppInit: User validation failed', {
					level: SENTRY_ERROR_LEVELS.FATAL,
					extra: {
						error: error,
					},
				});
			}
		}

		// Flush deferred device ID events after validateUser completes
		try {
			await flushDeferredEvents();
		} catch (error) {
			Sentry.captureMessage('AppInit: Failed to flush deferred device ID events', {
				extra: {
					error: error,
				},
			});
		}
		disableEventDeferral();
	}

	/**
	 * Parses and validates the inbound-gateway payload when present on the current URL.
	 * @param {URL} url - Current page URL
	 * @param {boolean} isInboundGateway - Whether the current route is inbound-gateway
	 * @returns {Promise<InboundData | null>} Validated inbound payload, or null when not applicable
	 */
	private static async parseInboundPayload(
		url: URL,
		isInboundGateway: boolean
	): Promise<InboundData | null> {
		if (!isInboundGateway) {
			return null;
		}

		const payload = url.searchParams.get(QUERY_PARAM_KEYS.PAYLOAD);
		if (!payload) {
			return null;
		}

		try {
			const { validateInboundData } = await import('$lib/services/inbound-gateway/validation');
			return validateInboundData(payload);
		} catch (error) {
			Sentry.captureMessage('AppInit: Failed to parse inbound gateway payload', {
				level: SENTRY_ERROR_LEVELS.FATAL,
				extra: {
					error: error,
				},
			});
			return null;
		}
	}

	/**
	 * Derives link-from-app meta from a validated inbound-gateway payload.
	 * Parses the payload early to set link-from-app meta before experiments initialize.
	 * @param {InboundData | null} inboundPayload - Validated inbound-gateway payload
	 * @returns {{ isLinkFromApp: boolean; isLinkFromAppPlatform: string | null }} Link-from-app meta values
	 */
	private static isLinkFromApp(inboundPayload: InboundData | null): {
		isLinkFromApp: boolean;
		isLinkFromAppPlatform: string | null;
	} {
		const defaultLinkFromAppValues = {
			isLinkFromApp: false,
			isLinkFromAppPlatform: null,
		};

		if (inboundPayload?.platform) {
			return {
				isLinkFromApp: isEntryFromAltPaymentAppLink(inboundPayload.platform),
				isLinkFromAppPlatform: inboundPayload.platform,
			};
		}

		return defaultLinkFromAppValues;
	}

	/**
	 * Initializes meta values (including inApp detection) BEFORE route conditions run.
	 * Route conditions depend on config.meta.inApp, so this must happen early.
	 * @param {string} deviceId - Device ID
	 * @returns {Promise<void>}
	 */
	private static async initializeMeta(deviceId: string): Promise<void> {
		try {
			await setMeta({
				...Cookies.get(),
				acquisitionSource: AnalyticsService.getSourceTag() ?? '',
				deviceId,
				environment: getEnvironment(),
			});
		} catch (error) {
			Sentry.captureMessage('AppInit: Failed to set meta values', {
				level: SENTRY_ERROR_LEVELS.FATAL,
				extra: {
					error: error,
				},
			});
		}
	}

	/**
	 * Initializes global store with app-level state.
	 * @param {URLSearchParams} params - URL search parameters
	 * @param {boolean} isFirstTimeVisit - Whether this is a first-time visit
	 * @param {InboundData | null} inboundPayload - Validated inbound-gateway payload
	 * @returns {Promise<void>}
	 */
	private static async initializeGlobalStore(
		params: URLSearchParams,
		isFirstTimeVisit: boolean,
		inboundPayload: InboundData | null
	): Promise<void> {
		/**
		 * linkFromAppPlatform (string|null)only comes from top-level query string when referred
		 * from the 1.0 app.  This param is overwritten by the inbound-gateway payload in the
		 * inbound-gateway +page.js by a combination of the payload referrer and platform.
		 * If already set in the store and not overwritten by the inbound-gateway payload, this
		 * value will persist on any request, whether the user is in an app-served flow or not,
		 * unless the global store is cleared.
		 */
		const linkFromAppPlatform = params.get(QUERY_PARAM_KEYS.LINK_FROM_APP_PLATFORM);
		const isLinkFromEmail = params.get(QUERY_PARAM_KEYS.EMAIL_CLICK) ? 'true' : 'false';
		/**
		 * isLinkFromApp (boolean) and isLinkFromAppPlatform (string|null) are derived from the
		 * inbound-gateway payload and not updated by any subsequent process other than when
		 * the global store is cleared.
		 */
		const { isLinkFromApp, isLinkFromAppPlatform } = this.isLinkFromApp(inboundPayload);
		const currentLinkFromAppPlatform = get(app).global.isLinkFromAppPlatform;

		await setGlobalStore({
			...(linkFromAppPlatform && { linkFromAppPlatform }),
			isFirstTimeVisit,
			isLinkFromEmail,
			isLinkFromApp: get(app).global.isLinkFromApp ? true : isLinkFromApp,
			isLinkFromAppPlatform:
				typeof currentLinkFromAppPlatform === 'string' &&
				currentLinkFromAppPlatform !== LINK_FROM_APP_PLATFORMS.DEFAULT
					? currentLinkFromAppPlatform
					: isLinkFromAppPlatform,
		});
	}

	/**
	 * Initializes feature flags (qualifies feature flags and experiment arms)
	 * @returns {Promise<void>}
	 */
	private static async initializeFeatureFlags(): Promise<void> {
		await qualifyFeatureFlags();
	}

	/**
	 * Centralized token introspection for app initialization.
	 * Handles three token sources:
	 * 1. QUERY_PARAM_KEYS.ABCM_TOKEN - renamed token used during PayPal redirect flows
	 * 2. QUERY_PARAM_KEYS.TOKEN - standard token param (ignored when pp_app_switch is present, as that indicates a PayPal-appended token)
	 * 3. Pre-validated inbound-gateway payload token
	 * @param {boolean} shouldPassToken - When true, keep the standard `token` query param in the URL for downstream handlers (e.g. qr-entry, link-entry)
	 * @param {boolean} [preserveSessionDeviceId] - Whether to preserve the session device id, forwarded to the introspect request
	 * @param {InboundData | null} [inboundPayload] - Pre-validated inbound-gateway payload
	 * @returns {Promise<{ outcome: TokenIntrospectResult; validated: boolean }>} Whether initialization should continue to page or redirect, and whether the user store was hydrated from the introspection response.
	 */
	private static async introspectTokenFromUrl(
		shouldPassToken: boolean,
		preserveSessionDeviceId?: boolean,
		inboundPayload: InboundData | null = null
	): Promise<{ outcome: TokenIntrospectResult; validated: boolean }> {
		const url = new URL(window.location.href);

		// HACK: PayPal's app switch redirect appends its own TOKEN param to the URL,
		// which collides with our auth token param. PaypalService works around this by
		// renaming our token to ABCM_TOKEN before the redirect. So we check ABCM_TOKEN
		// first, and only fall back to TOKEN if this isn't a PayPal redirect.
		const abcmToken = url.searchParams.get(QUERY_PARAM_KEYS.ABCM_TOKEN);
		const isPaypalRedirect = url.searchParams.has(PAYPAL_QUERY_PARAM_KEYS.PAYPAL_REDIRECT_FLAG);
		const standardToken = isPaypalRedirect ? null : url.searchParams.get(QUERY_PARAM_KEYS.TOKEN);
		let token = abcmToken ?? standardToken;
		const source = url.searchParams.get(QUERY_PARAM_KEYS.SOURCE);

		// Check for token from pre-validated inbound-gateway payload
		if (!token && inboundPayload?.token) {
			token = inboundPayload.token;
		}

		if (!token) {
			return { outcome: TOKEN_INTROSPECT_RESULT.CONTINUE, validated: false };
		}

		// Strip token if it's not needed for downstream handlers
		if (!shouldPassToken && standardToken) {
			this.removeQueryParamsFromCurrentUrl([QUERY_PARAM_KEYS.TOKEN]);
		}
		const introspectOptions: { preserveSessionDeviceId?: boolean } = {};
		if (preserveSessionDeviceId !== undefined) {
			introspectOptions.preserveSessionDeviceId = preserveSessionDeviceId;
		}
		const { error, validated } = await tokenIntrospect(token, source ?? '', introspectOptions);

		if (error) {
			const code = error instanceof APIError ? Number(error.errorCode) : NaN;
			if (code === 992415 || code === 992416) {
				return { outcome: TOKEN_INTROSPECT_RESULT.REDIRECT_TO_LOGIN, validated };
			}
		}

		return { outcome: TOKEN_INTROSPECT_RESULT.CONTINUE, validated };
	}

	/**
	 * Removes query parameters from the current URL without a page reload.
	 * @param {string[]} keys - Query param keys to remove.
	 * @returns {void}
	 */
	private static removeQueryParamsFromCurrentUrl(keys: string[]): void {
		const currentUrl = new URL(window.location.href);
		const previousSearch = currentUrl.search;
		keys.forEach((key) => currentUrl.searchParams.delete(key));
		if (currentUrl.search === previousSearch) {
			return;
		}

		const search = currentUrl.searchParams.toString();
		const updatedUrl = `${currentUrl.pathname}${search ? `?${search}` : ''}${currentUrl.hash}`;
		window.history.replaceState(window.history.state, '', updatedUrl);
	}

	/**
	 * Sends the user to login with `error_type=token_expired` and the current destination as `redirect_to`.
	 * @returns {Promise<void>}
	 */
	private static async redirectToLoginAfterIntrospectFailure(): Promise<void> {
		const destination = `${window.location.pathname}${window.location.search}${window.location.hash}`;
		const params = new URLSearchParams();
		params.set(QUERY_PARAM_KEYS.ERROR_TYPE, LOGIN_ERROR_TYPES.TOKEN_EXPIRED);
		params.set(LOGIN_URL_QUERY_KEYS.REDIRECT_TO, destination);
		await goto(`${PAGES.INTERNAL.LOGIN}?${params.toString()}`);
	}
}
